import { useMemo } from 'react';

import type { ListItemType } from 'components/ui/lists/CommonList';

import MenuButtonItem from 'components/ui/menus/MenuButtonItem';
import { formatTitleString } from 'utils/formatUtils';

export interface MenuItemConfig extends Omit<ListItemType, 'labelElement'> {
  /**
   * Whether the menu item is disabled and cannot be clicked
   */
  disabled?: boolean;
  /**
   * Whether or not to check the `archived` property of the item when `disabled`.
   * Disabled for special conditions e.g. the `archive` button itself and items with no `archive` property.
   */
  isArchivable?: boolean;
  /**
   * Click handler function called when the menu item is selected
   */
  onClick: () => void;
  /**
   * Optional mouse enter handler, e.g. for prefetching data on hover
   */
  onMouseEnter?: () => void;
}

interface Props {
  /**
   * Whether the entity owning the menu is archived, which drops every item that did not opt out.
   */
  isArchived?: boolean;
  items: MenuItemConfig[];
}

/**
 * Renders a menu's items from config, dropping the ones the current entity state disallows.
 *
 * Filtering here rather than greying items out is what lets a caller hand over a full action list
 * and get an empty menu — and, in turn, a hidden trigger — without writing a permission guard of its
 * own.
 */
const MenuItems = ({ isArchived, items }: Props) => {
  const visibleItems = useMemo(
    () => items.filter(({ disabled, isArchivable = true }) => (isArchivable ? !isArchived && !disabled : !disabled)),
    [isArchived, items]
  );

  return (
    <>
      {visibleItems.map(
        ({ icon = null, iconPosition, label, listItemElementOverride, onClick, onMouseEnter, testId, titleCss }) => (
          <MenuButtonItem
            icon={icon}
            iconPosition={iconPosition}
            key={label}
            label={formatTitleString(label)}
            listItemElementOverride={listItemElementOverride}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            testId={testId}
            titleCss={titleCss}
          />
        )
      )}
    </>
  );
};

export default MenuItems;
